if (!customElements.get('scroll-to-top-button')) {
  class ScrollToTopButton extends HTMLElement {
    constructor() {
      super();
      this.isRevealed = false;
      this.onScrollHandler = this.onScroll.bind(this);
      this.onClickHandler = this.scrollToTop.bind(this);
      this.onKeyDownHandler = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      // Initial state
      this.updateVisibility();

      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      this.addEventListener('click', this.onClickHandler);
      this.addEventListener('keydown', this.onKeyDownHandler);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
      this.removeEventListener('click', this.onClickHandler);
      this.removeEventListener('keydown', this.onKeyDownHandler);
    }

    onScroll() {
      // Schedule all DOM reads/writes in one animation frame
      if (this._ticking) return;
      this._ticking = true;

      requestAnimationFrame(() => {
        this._ticking = false;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      // Read scroll position once per frame, not on every event
      const shouldReveal = window.scrollY > window.innerHeight;
      if (shouldReveal !== this.isRevealed) {
        this.isRevealed = shouldReveal;
        this.classList.toggle('reveal', shouldReveal);
      }
    }

    scrollToTop() {
      // RS0930-SECT-D04 — honour prefers-reduced-motion: jump instantly instead
      // of smooth-scrolling. 'instant' (not 'auto') so a CSS scroll-behavior
      // on <html> cannot re-introduce the animation. With no animation to wait
      // for, focus moves to the skip link immediately; the 700ms heuristic is
      // kept only for the smooth path.
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'instant' : 'smooth' });

      // Wait until scroll completes before focusing (simple heuristic)
      setTimeout(() => {
        document.querySelector('.skip-to-content-link')?.focus();
      }, reduceMotion ? 0 : 700);
    }

    onKeyDown(event) {
      // W5-A11Y-D07 — the host element now carries role="button" (see
      // sections/scroll-to-top.liquid). Anything exposed as a button must
      // activate on BOTH Enter and Space; only Enter was handled here, so a
      // keyboard user who pressed Space got a page scroll instead of the
      // control's action. Space is the default-scroll key, so the default is
      // prevented before acting. `event.key === ' '` is the modern value;
      // 'Spacebar' is kept for older engines that still report the DOM 3 name.
      if (event.key === 'Enter') {
        this.scrollToTop();
        return;
      }

      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        this.scrollToTop();
      }
    }
  }

  customElements.define('scroll-to-top-button', ScrollToTopButton);
}